/**
 * EntryModelsTab Component
 *
 * Main container for the Entry Models tab in ReferenceDataPage.
 * Provides full CRUD functionality for entry model items with:
 * - List display with category badges and "used in" counts
 * - Create/Edit modal
 * - Archive/Restore actions with confirmation
 * - Show Archived toggle
 * - Filter by category (multi-select) and usage
 * - Sort options
 * - Cross-tab sync subscription
 * - Responsive: desktop inline filters, mobile bottom sheet
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Eye, EyeOff, AlertTriangle, RefreshCw, SlidersHorizontal, ArrowUp, ArrowDown } from 'lucide-react';

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  EmptyState,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
  useDisclosure,
} from '@enfomentis/ui';
import { useIsDesktop } from '@/hooks';
import { entryModelService } from '@/services/EntryModelService';
import { entryModelCategoryService } from '@/services/EntryModelCategoryService';
import { playbookService } from '@/services/PlaybookService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { EntryModelCategory } from '@/types/schema/EntryModelCategory';
import type { Playbook } from '@/types/schema/Playbook';

import { EntryModelListItem } from './EntryModelListItem';
import { EntryModelModal } from './EntryModelModal';

// Filter constants
const NO_CATEGORY_KEY = '__none__';
type UsageFilter = 'all' | 'used' | 'unused';
type SortField = 'name' | 'category' | 'usage';
type SortDir = 'asc' | 'desc';

const SORT_FIELDS: { key: SortField; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'usage', label: 'Used' },
];

const USAGE_OPTIONS: { key: UsageFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'used', label: 'Used' },
  { key: 'unused', label: 'Unused' },
];

export const EntryModelsTab: React.FC = () => {
  const isDesktop = useIsDesktop();

  // Data state
  const [entryModels, setEntryModels] = useState<EntryModel[]>([]);
  const [categories, setCategories] = useState<EntryModelCategory[]>([]);
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // UI state
  const [showArchived, setShowArchived] = useState(false);
  const [editingEntryModel, setEditingEntryModel] = useState<EntryModel | undefined>();
  const [archivingEntryModel, setArchivingEntryModel] = useState<EntryModel | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  // Filter/sort state
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [usageFilter, setUsageFilter] = useState<UsageFilter>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Mobile filter sheet staged state (filters only - sort is immediate via header buttons)
  const [stagedCategoryFilter, setStagedCategoryFilter] = useState<string[]>([]);
  const [stagedUsageFilter, setStagedUsageFilter] = useState<UsageFilter>('all');
  const [stagedShowArchived, setStagedShowArchived] = useState(false);

  // Portal container state for mobile filter sheet (fixes aria-hidden warning)
  // Using state instead of ref ensures re-render when container mounts
  const [mobileFilterPortalContainer, setMobileFilterPortalContainer] = useState<HTMLDivElement | null>(null);
  const setMobileFilterPortalRef = useCallback((node: HTMLDivElement | null) => {
    setMobileFilterPortalContainer(node);
  }, []);

  // Modal state
  const modalDisclosure = useDisclosure();
  const filterSheetDisclosure = useDisclosure();

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const [allEntryModels, allCategories, allPlaybooks] = await Promise.all([
        entryModelService.getAll(),
        entryModelCategoryService.getAll(),
        playbookService.getAll(),
      ]);
      setEntryModels(allEntryModels);
      setCategories(allCategories);
      setPlaybooks(allPlaybooks);
    } catch (error) {
      console.error('Failed to load entry model data:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscribe to cross-tab sync for entry model changes
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('entry_models', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Subscribe to category changes (for badge updates)
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('entry_model_categories', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Subscribe to playbook changes (for "used in" count updates)
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('playbooks', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Build usedInMap: entryModelId -> playbookNames[] (sorted alphabetically)
  const usedInMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const playbook of playbooks) {
      if (playbook.entryModelIds) {
        for (const entryModelId of playbook.entryModelIds) {
          const names = map.get(entryModelId) || [];
          names.push(playbook.name);
          map.set(entryModelId, names);
        }
      }
    }
    // Sort each array alphabetically (case-insensitive)
    for (const [key, names] of map) {
      map.set(key, names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
    }
    return map;
  }, [playbooks]);

  // Build categoryMap for O(1) lookups
  const categoryMap = useMemo(() => {
    return new Map(categories.map(c => [c.id, c]));
  }, [categories]);

  // Active categories for modal dropdowns (edit form)
  const activeCategories = useMemo(() => {
    return categories.filter(c => c.status !== 'archived');
  }, [categories]);

  // Dynamic filter category options: include archived categories when showArchived is ON
  const filterCategoryOptions = useMemo(() => {
    return showArchived ? categories : categories.filter(c => c.status !== 'archived');
  }, [categories, showArchived]);

  // Mobile filter category options: react to stagedShowArchived (before Apply)
  const mobileFilterCategoryOptions = useMemo(() => {
    return stagedShowArchived ? categories : categories.filter(c => c.status !== 'archived');
  }, [categories, stagedShowArchived]);

  // Clean up category filter when showArchived toggles to false:
  // Remove any archived category IDs that are no longer visible as filter options
  useEffect(() => {
    if (showArchived || categories.length === 0) return;
    const archivedIds = new Set(
      categories.filter(c => c.status === 'archived').map(c => c.id)
    );
    if (archivedIds.size === 0) return;
    setCategoryFilter(prev => {
      const cleaned = prev.filter(id => !archivedIds.has(id));
      return cleaned.length === prev.length ? prev : cleaned;
    });
  }, [showArchived, categories]);

  // Check if there are any archived entry models
  const hasArchivedEntryModels = useMemo(() => {
    return entryModels.some(em => em.status === 'archived');
  }, [entryModels]);

  // Check if any filters are active (for mobile badge - includes showArchived for badge indicator)
  const hasActiveFilters = useMemo(() => {
    return categoryFilter.length > 0 || usageFilter !== 'all' || showArchived;
  }, [categoryFilter, usageFilter, showArchived]);

  // Check if content-excluding filters are active (for empty state message - excludes showArchived)
  const hasContentFilters = useMemo(() => {
    return categoryFilter.length > 0 || usageFilter !== 'all';
  }, [categoryFilter, usageFilter]);

  // Filter and sort entry models
  const sortedEntryModels = useMemo(() => {
    // Step 1: Apply archived filter FIRST
    let filtered = showArchived
      ? entryModels
      : entryModels.filter(em => em.status !== 'archived');

    // Step 2: Apply category filter
    if (categoryFilter.length > 0) {
      filtered = filtered.filter(em => {
        const catId = em.entryModelCategoryId;
        if (categoryFilter.includes(NO_CATEGORY_KEY) && !catId) {
          return true;
        }
        return catId && categoryFilter.includes(catId);
      });
    }

    // Step 3: Apply usage filter
    if (usageFilter === 'used') {
      filtered = filtered.filter(em => (usedInMap.get(em.id)?.length ?? 0) > 0);
    } else if (usageFilter === 'unused') {
      filtered = filtered.filter(em => (usedInMap.get(em.id)?.length ?? 0) === 0);
    }

    // Step 4: Sort
    return [...filtered].sort((a, b) => {
      // Active items always first (above sort comparator)
      if (a.status === 'archived' && b.status !== 'archived') return 1;
      if (a.status !== 'archived' && b.status === 'archived') return -1;

      // Direction multiplier: 1 for asc, -1 for desc
      const dirMult = sortDir === 'asc' ? 1 : -1;

      // Then apply selected sort field
      switch (sortField) {
        case 'category': {
          const catA = categoryMap.get(a.entryModelCategoryId || '')?.name || '';
          const catB = categoryMap.get(b.entryModelCategoryId || '')?.name || '';
          const catCompare = catA.localeCompare(catB, undefined, { sensitivity: 'base' });
          if (catCompare !== 0) return catCompare * dirMult;
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        }
        case 'usage': {
          const usageA = usedInMap.get(a.id)?.length ?? 0;
          const usageB = usedInMap.get(b.id)?.length ?? 0;
          if (usageA !== usageB) return (usageA - usageB) * dirMult;
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        }
        case 'name':
        default:
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) * dirMult;
      }
    });
  }, [entryModels, showArchived, categoryFilter, usageFilter, sortField, sortDir, usedInMap, categoryMap]);

  // Handlers
  const handleAddClick = () => {
    setEditingEntryModel(undefined);
    modalDisclosure.onOpen();
  };

  const handleEditClick = (entryModel: EntryModel) => {
    setEditingEntryModel(entryModel);
    modalDisclosure.onOpen();
  };

  const handleSave = async (data: { name: string; description?: string; entryModelCategoryId?: string }) => {
    setIsSaving(true);
    try {
      if (editingEntryModel) {
        await entryModelService.update(editingEntryModel.id, data);
        syncCoordinatorService.notifyChange('entry_models', editingEntryModel.id);
      } else {
        const newEntryModel = await entryModelService.add(data);
        syncCoordinatorService.notifyChange('entry_models', newEntryModel.id);
      }
      await loadData();
      modalDisclosure.onClose();
    } catch (error) {
      console.error('Failed to save entry model:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveClick = (entryModel: EntryModel) => {
    setArchivingEntryModel(entryModel);
  };

  const handleArchiveConfirm = async () => {
    if (!archivingEntryModel) return;
    try {
      await entryModelService.update(archivingEntryModel.id, { status: 'archived' });
      syncCoordinatorService.notifyChange('entry_models', archivingEntryModel.id);
      await loadData();
    } catch (error) {
      console.error('Failed to archive entry model:', error);
    } finally {
      setArchivingEntryModel(undefined);
    }
  };

  const handleArchiveCancel = () => {
    setArchivingEntryModel(undefined);
  };

  const handleRestore = async (entryModel: EntryModel) => {
    try {
      await entryModelService.update(entryModel.id, { status: 'active' });
      syncCoordinatorService.notifyChange('entry_models', entryModel.id);
      await loadData();
    } catch (error) {
      console.error('Failed to restore entry model:', error);
    }
  };

  // Mobile filter sheet handlers (sort is excluded - it's immediate via header buttons)
  const handleOpenFilterSheet = () => {
    // Stage current filter values
    setStagedCategoryFilter(categoryFilter);
    setStagedUsageFilter(usageFilter);
    setStagedShowArchived(showArchived);
    filterSheetDisclosure.onOpen();
  };

  const handleApplyFilters = () => {
    setCategoryFilter(stagedCategoryFilter);
    setUsageFilter(stagedUsageFilter);
    setShowArchived(stagedShowArchived);
    filterSheetDisclosure.onClose();
  };

  const handleResetFilters = () => {
    setStagedCategoryFilter([]);
    setStagedUsageFilter('all');
    setStagedShowArchived(false);
  };

  // Sort button handler: toggle direction if same field, switch if different
  const handleSortClick = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card radius="none" shadow="none">
        <CardBody className="px-4 py-4">
          <div className="text-center py-8">
            <p className="text-text-secondary">Loading entry models...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Error state
  if (loadError) {
    return (
      <Card radius="none" shadow="none">
        <CardBody className="px-4 py-4">
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="flex items-center gap-2 text-danger-600">
              <AlertTriangle size={20} />
              <p className="text-sm">Failed to load entry models</p>
            </div>
            <Button
              size="sm"
              variant="flat"
              startContent={<RefreshCw size={16} />}
              onPress={loadData}
            >
              Retry
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Empty state
  if (entryModels.length === 0) {
    return (
      <>
        <Card radius="none" shadow="none">
          <CardBody className="px-4 py-4">
            <EmptyState
              title="No entry models yet"
              description="Entry models define your precise trigger and execution method — when to enter and how. Add entry models to clarify your decision rules."
              action={
                <Button
                  variant="light"
                  startContent={<Plus size={16} />}
                  onPress={handleAddClick}
                >
                  Add Entry Model
                </Button>
              }
            />
          </CardBody>
        </Card>
        <EntryModelModal
          isOpen={modalDisclosure.isOpen}
          onClose={modalDisclosure.onClose}
          entryModel={editingEntryModel}
          categories={activeCategories}
          allCategories={categories}
          onSave={handleSave}
          isSaving={isSaving}
        />
      </>
    );
  }

  // Render filter UI based on device
  const renderDesktopFilters = () => (
    <div className="flex flex-wrap items-center justify-between w-full gap-3">
      {/* Filters Group */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-text-tertiary mr-1">Filters:</span>
        {/* Category Filter (multi-select) */}
        <Select
          size="sm"
          variant="bordered"
          placeholder="Category"
          aria-label="Filter by category"
          selectionMode="multiple"
          selectedKeys={new Set(categoryFilter)}
          onSelectionChange={(keys) => setCategoryFilter(Array.from(keys) as string[])}
          className="min-w-30"
          popoverProps={{ isNonModal: true, placement: 'bottom-start' }}
        >
          {[
            <SelectItem key={NO_CATEGORY_KEY} textValue="No Category">
              <span className="text-text-secondary">No Category</span>
            </SelectItem>,
            ...filterCategoryOptions.map((cat) => (
              <SelectItem key={cat.id} textValue={cat.name}>
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: cat.color }}
                  />
                  <span>{cat.name}</span>
                  {cat.status === 'archived' && (
                    <span className="text-xs text-text-tertiary">(Archived)</span>
                  )}
                </div>
              </SelectItem>
            )),
          ]}
        </Select>

        {/* Usage Filter - Segmented Buttons */}
        <div className="flex items-center border border-default-200 rounded-lg">
          {USAGE_OPTIONS.map((opt) => (
            <Button
              key={opt.key}
              size="sm"
              variant="light"
              radius="none"
              onPress={() => setUsageFilter(opt.key)}
              className={
                usageFilter === opt.key
                  ? 'bg-default-100 text-primary font-medium flex-1'
                  : 'text-text-secondary flex-1'
              }
            >
              {opt.label}
            </Button>
          ))}
        </div>

        {/* Archived Toggle */}
        {hasArchivedEntryModels && (
          <Button
            size="sm"
            variant="flat"
            startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
            onPress={() => setShowArchived(!showArchived)}
            className={`bg-default-100 shrink-0 min-w-max ${  showArchived ? 'text-text-primary' : 'text-text-tertiary'}`}
          >
            Archived
          </Button>
        )}
      </div>

      {/* Sort Buttons */}
      <div className="flex items-center gap-1">
        <span className="text-xs text-text-tertiary mr-1">Sort:</span>
        {SORT_FIELDS.map((field) => (
          <Button
            key={field.key}
            size="sm"
            variant="light"
            onPress={() => handleSortClick(field.key)}
            className={
              sortField === field.key
                ? 'bg-default-100 text-primary font-medium'
                : 'text-text-secondary'
            }
            endContent={
              sortField === field.key ? (
                sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
              ) : undefined
            }
          >
            {field.label}
          </Button>
        ))}
      </div>

      {/* Add Button */}
      <Button
        variant="light"
        size="sm"
        startContent={<Plus size={16} />}
        onPress={handleAddClick}
        className="shrink-0"
      >
        Add Entry Model
      </Button>
    </div>
  );

  const renderMobileFilters = () => (
    <div className="flex items-center justify-between w-full gap-2">
      {/* Filter Icon Button with badge */}
      <Button
        size="sm"
        variant="light"
        isIconOnly
        onPress={handleOpenFilterSheet}
        aria-label="Filter"
        className="relative shrink-0"
      >
        <SlidersHorizontal size={16} />
        {hasActiveFilters && (
          <span className="absolute top-0 right-0 w-2 h-2 bg-primary rounded-full" />
        )}
      </Button>

      {/* Sort Buttons - same as desktop */}
      <div className="flex items-center gap-1 flex-1 justify-center">
        {SORT_FIELDS.map((field) => (
          <Button
            key={field.key}
            size="sm"
            variant="light"
            onPress={() => handleSortClick(field.key)}
            className={
              sortField === field.key
                ? 'bg-default-100 text-primary font-medium px-2'
                : 'text-text-secondary px-2'
            }
            endContent={
              sortField === field.key ? (
                sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
              ) : undefined
            }
          >
            {field.label}
          </Button>
        ))}
      </div>

      <Button
        variant="light"
        size="sm"
        startContent={<Plus size={16} />}
        onPress={handleAddClick}
        className="shrink-0"
      >
        Add
      </Button>
    </div>
  );

  return (
    <>
      {/* Subtitle - above filters */}
      <p
        className="text-xs text-text-secondary truncate px-4 py-2"
        title="Defines when and how you enter a trade once criteria are met"
      >
        Defines when and how you enter a trade once criteria are met
      </p>

      <Card radius="none" shadow="none">
        {/* Header with filters */}
        <CardHeader className="py-3 border-b border-default-200">
          {isDesktop ? renderDesktopFilters() : renderMobileFilters()}
        </CardHeader>

        <CardBody className="px-0 py-4">
          {/* Entry Models List */}
          {sortedEntryModels.length > 0 && (
            <div className="space-y-2">
              {sortedEntryModels.map((entryModel) => (
                <Card
                  key={entryModel.id}
                  radius="sm"
                  shadow="none"
                  className="border border-default-200"
                >
                  <CardBody className="p-0">
                    <EntryModelListItem
                      entryModel={entryModel}
                      category={categoryMap.get(entryModel.entryModelCategoryId || '')}
                      playbookNames={usedInMap.get(entryModel.id) || []}
                      onEdit={() => handleEditClick(entryModel)}
                      onArchiveClick={() => handleArchiveClick(entryModel)}
                      onRestore={() => handleRestore(entryModel)}
                    />
                  </CardBody>
                </Card>
              ))}
            </div>
          )}

          {/* No results from filters */}
          {sortedEntryModels.length === 0 && entryModels.length > 0 && (
            <EmptyState
              title="No matching entry models"
              description={
                hasContentFilters
                  ? "No entry models match the current filters."
                  : "All entry models are archived. Enable 'Archived' to view them."
              }
            />
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <EntryModelModal
        isOpen={modalDisclosure.isOpen}
        onClose={modalDisclosure.onClose}
        entryModel={editingEntryModel}
        categories={activeCategories}
        allCategories={categories}
        onSave={handleSave}
        isSaving={isSaving}
      />

      {/* Archive Confirmation Modal */}
      <Modal
        isOpen={!!archivingEntryModel}
        onOpenChange={(open) => !open && handleArchiveCancel()}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader>Archive Entry Model</ModalHeader>
          <ModalBody>
            <p className="text-text-secondary">
              Are you sure you want to archive &quot;{archivingEntryModel?.name}&quot;?
            </p>
            <p className="text-sm text-text-tertiary">
              Archived entry models remain functional in playbooks that reference them.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleArchiveCancel}>
              Cancel
            </Button>
            <Button color="warning" onPress={handleArchiveConfirm}>
              Archive
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Mobile Filter Sheet - filters only, sort is in header */}
      <Modal
        isOpen={filterSheetDisclosure.isOpen}
        onOpenChange={(open) => !open && filterSheetDisclosure.onClose()}
        size="full"
        placement="bottom"
        classNames={{
          wrapper: 'items-end',
          base: 'rounded-t-xl rounded-b-none max-h-[60vh]',
        }}
      >
        <ModalContent>
          <ModalHeader className="flex justify-between items-center">
            <span>Filter</span>
            <Button size="sm" variant="light" onPress={handleResetFilters}>
              Reset
            </Button>
          </ModalHeader>
          <ModalBody className="gap-4">
            <div ref={setMobileFilterPortalRef} className="flex flex-col gap-4">
              {/* Category Filter */}
              <Select
                label="Category"
                variant="bordered"
                selectionMode="multiple"
                selectedKeys={new Set(stagedCategoryFilter)}
                onSelectionChange={(keys) => setStagedCategoryFilter(Array.from(keys) as string[])}
                popoverProps={{
                  isNonModal: true,
                  shouldBlockScroll: false,
                  placement: 'bottom-start',
                  ...(mobileFilterPortalContainer ? { portalContainer: mobileFilterPortalContainer } : {}),
                }}
              >
                {[
                  <SelectItem key={NO_CATEGORY_KEY} textValue="No Category">
                    <span className="text-text-secondary">No Category</span>
                  </SelectItem>,
                  ...mobileFilterCategoryOptions.map((cat) => (
                    <SelectItem key={cat.id} textValue={cat.name}>
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span>{cat.name}</span>
                        {cat.status === 'archived' && (
                          <span className="text-xs text-text-tertiary">(Archived)</span>
                        )}
                      </div>
                    </SelectItem>
                  )),
                ]}
              </Select>

              {/* Usage Filter - Segmented Buttons */}
              <div className="space-y-2">
                <span className="text-sm text-text-secondary">Usage</span>
                <div className="flex items-center border border-default-200 rounded-lg overflow-hidden">
                  {USAGE_OPTIONS.map((opt) => (
                    <Button
                      key={opt.key}
                      size="sm"
                      variant="light"
                      radius="none"
                      onPress={() => setStagedUsageFilter(opt.key)}
                      className={
                        stagedUsageFilter === opt.key
                          ? 'bg-default-100 text-primary font-medium flex-1'
                          : 'text-text-secondary flex-1'
                      }
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Show Archived Toggle */}
              {hasArchivedEntryModels && (
                <Button
                  size="sm"
                  variant="flat"
                  startContent={stagedShowArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                  onPress={() => setStagedShowArchived(!stagedShowArchived)}
                  className={stagedShowArchived ? 'bg-default-100 text-text-primary' : 'bg-default-100 text-text-tertiary'}
                >
                  Archived
                </Button>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={filterSheetDisclosure.onClose}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleApplyFilters}>
              Apply
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
